import React, { useState } from "react";
import { 
  Box, 
  Card, 
  CardContent, 
  TextField, 
  Button, 
  IconButton, 
  Typography, 
  Avatar,
  Chip,
  Fade,
  Collapse,
  Divider,
  styled
} from "@mui/material";
import { useMutation, useQuery } from "@apollo/client";
import { FormattedMessage, useIntl } from "react-intl";
import { ProfilePicture } from "../../../components/common/profile-picture";
import { IoMdThumbsDown, IoMdThumbsUp } from "react-icons/io";
import { Comment } from "./comment";
import { CommentInput } from "./comment-input";
import {
  ADD_COMMENT_FOR_TEACHER,
  DELETE_COMMENT_FOR_TEACHER,
  EDIT_COMMENT_FOR_TEACHER,
  TOGGLE_TEACHING_LIKE,
  TOGGLE_TEACHING_DISLIKE,
} from "../../../apollo-client/mutations";
import {
  GET_TEACHING_DETAIL,
  GET_TEACHING_COMMENTS,
  GET_USER_HEADER,
} from "../../../apollo-client/queries";
import { CancelButton, SendButton } from "./comment-buttons";
import { FetchMoreOnClick } from "./fetch-more-on-click";
import { PrimarySpinner } from "../../../components/spinner/primary-spinner";
import { CONTENT_TYPES } from "../../../constants";

export const TeacherDetailComments = ({ teaching }) => {
  const { _id, likeCount, dislikeCount, teacherName } = teaching;

  const [comment, setComment] = useState("");
  const [areCommentButtonsDisplayed, setAreCommentButtonsDisplayed] =
    useState(false);

  const intl = useIntl();

  const { data, loading, error, fetchMore } = useQuery(GET_TEACHING_COMMENTS, {
    variables: { _id, limit: 15 },
    // notifyOnNetworkStatusChange: true,
  });

  const { data: getUserData, loading: getUserLoading } =
    useQuery(GET_USER_HEADER);

  const [addComment] = useMutation(ADD_COMMENT_FOR_TEACHER, {
    refetchQueries: [GET_TEACHING_COMMENTS],
    onCompleted: () => {
      handleClean();
    },
  });

  const [deleteComment] = useMutation(DELETE_COMMENT_FOR_TEACHER, {
    refetchQueries: [GET_TEACHING_COMMENTS],
  });
  const [editComment] = useMutation(EDIT_COMMENT_FOR_TEACHER);

  const [toggleLike] = useMutation(TOGGLE_TEACHING_LIKE, {
    refetchQueries: [GET_TEACHING_DETAIL, GET_TEACHING_COMMENTS],
  });
  const [toggleDislike] = useMutation(TOGGLE_TEACHING_DISLIKE, {
    refetchQueries: [GET_TEACHING_DETAIL, GET_TEACHING_COMMENTS],
  });

  const handlePostComment = async () => {
    if (comment !== "")
      await addComment({
        variables: {
          contentId: _id,
          contentType: "teaching",
          comment: comment,
        },
      });
  };

  const handleDeleteComment = async (commentId) => {
    await deleteComment({
      variables: { _id: commentId },
    });
  };

  const handleLikeTeaching = async () => {
    await toggleLike({
      variables: {
        contentId: teaching._id,
        contentType: CONTENT_TYPES.TEACHING,
      },
    });
  };
  const handleDislikeTeaching = async () => {
    await toggleDislike({
      variables: {
        contentId: teaching._id,
        contentType: CONTENT_TYPES.TEACHING,
      },
    });
  };

  const handleClean = () => {
    setComment("");
    document.getElementById("comment_input" + _id).innerText = "";
    handleShowCommentButtons(false);
  };

  const handleShowCommentButtons = (boolean) => {
    setAreCommentButtonsDisplayed(boolean);
  };

  const handleFetchMoreComments = async () => {
    await fetchMore({
      variables: {
        limit: 15,
        skip: data.teachings.teachings[0].comments.comments.length,
      },
    });
  };

  return (
    <TeacherDetailsContainer>
      <ContentWrapper>
        <CenterWrapper>
          <ToggleCommentsThumbsWrapper>
            <CommentInputWrapper>
              <InputPictureWrapper>
                <ProfilePicture
                  src={getUserData && getUserData.user.profilePicture}
                  width={42}
                  height={42}
                  margin={"0 15px 0 0"}
                />
                <CommentInput
                  width={"100%"}
                  id={_id}
                  comment={comment}
                  placeholder={intl.formatMessage({ id: "comments.comment" })}
                  onKeyUp={() => {
                    setComment(
                      document.getElementById("comment_input" + _id).innerText
                    );
                  }}
                  postText={handlePostComment}
                  clean={handleClean}
                  onClick={() => handleShowCommentButtons(true)}
                />
              </InputPictureWrapper>
            </CommentInputWrapper>
            <ButtonThumbsWrapper>
              <CommentButtonWrapper in={areCommentButtonsDisplayed} timeout={300}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CancelButton onClick={handleClean} />
                  <SendButton comment={comment} onClick={handlePostComment} />
                </Box>
              </CommentButtonWrapper>
              <ThumbsWrapper>
                <IconButton
                  onClick={handleLikeTeaching}
                  sx={{
                    color: teaching.isLiked ? '#1976d2' : '#4d4d4d',
                    opacity: teaching.isLiked ? 1 : 0.5,
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      backgroundColor: 'rgba(25, 118, 210, 0.04)',
                      transform: 'scale(1.1)',
                    },
                  }}
                >
                  <IoMdThumbsUp size={29} />
                </IconButton>
                <LikeCounterText>{likeCount}</LikeCounterText>
                <IconButton
                  onClick={handleDislikeTeaching}
                  sx={{
                    color: teaching.isDisliked ? '#d32f2f' : '#4d4d4d',
                    opacity: teaching.isDisliked ? 1 : 0.5,
                    marginTop: '12px',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      backgroundColor: 'rgba(211, 47, 47, 0.04)',
                      transform: 'scale(1.1)',
                    },
                  }}
                >
                  <IoMdThumbsDown size={29} />
                </IconButton>
                <LikeCounterText>{dislikeCount}</LikeCounterText>
              </ThumbsWrapper>
            </ButtonThumbsWrapper>
          </ToggleCommentsThumbsWrapper>

          <CommentsHeadline>
            <FormattedMessage id="comments.headline" />
          </CommentsHeadline>

          {/*
            isExpanded, und die Comments toggeln lassen. Evt die weiteren Kommentare sogar dann erst laden

           */}

          <div style={{ width: "100%" }}>
            {data &&
              !error &&
              data.teachings.teachings[0]?.comments?.comments?.map(
                (comment) => (
                  <Comment
                    key={comment._id}
                    teacherName={teacherName}
                    editMutation={editComment}
                    deleteComment={() => handleDeleteComment(comment._id)}
                    commentObject={comment}
                  />
                )
              )}
            {loading && <StyledPrimarySpinner />}
            <StyledFetchMoreOnClick
              shouldShow={
                !loading &&
                data &&
                data.teachings &&
                data.teachings.teachings[0]?.comments.commentsCount !==
                  data.teachings.teachings[0]?.comments?.comments.length
              }
              onClick={handleFetchMoreComments}
            />
          </div>
        </CenterWrapper>
      </ContentWrapper>
    </TeacherDetailsContainer>
  );
};

// Styled MUI components - keeping original names but using MUI as base
const TeacherDetailsContainer = styled(Card)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'flex-start',
  width: '100%',
  minHeight: '100%',
  margin: '60px auto 15px auto',
  boxShadow: '0 2px 7px rgba(0, 0, 0, 0.4)',
  borderRadius: '15px',
  paddingBottom: '50px',
  [theme.breakpoints.down('md')]: {
    boxShadow: '0px 0px 0px',
    borderRadius: 0,
  },
}));

const ContentWrapper = styled(CardContent)({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  width: '90%',
  marginTop: '10px',
});

const CenterWrapper = styled(Box)(({ theme }) => ({
  height: '100%',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  marginTop: '25px',
  marginLeft: '31px',
  [theme.breakpoints.down('sm')]: {
    marginLeft: 0,
  },
}));

const ToggleCommentsThumbsWrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  alignItems: 'flex-start',
});

const ButtonThumbsWrapper = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
});

const ThumbsWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
});

const ThumbsUpWrapper = styled(Box)(({ isLiked }) => ({
  color: '#4d4d4d',
  marginRight: '3px',
  opacity: isLiked ? 1 : 0.5,
}));

const ThumbsDownWrapper = styled(Box)(({ isDisliked }) => ({
  color: '#4d4d4d',
  marginRight: '3px',
  marginTop: '12px',
  opacity: isDisliked ? 1 : 0.5,
}));

const LikeCounterText = styled(Typography)({
  fontSize: '12px',
  fontWeight: 500,
  marginRight: '8px',
  '&:last-child': {
    marginRight: 0,
  },
});

const CommentsHeadline = styled(Typography)({
  fontSize: '17px',
  fontWeight: 600,
  lineHeight: '20px',
  color: '#383838',
  marginTop: '15px',
  marginBottom: '38px',
});

const CommentInputWrapper = styled(Box)({
  width: '100%',
});

const CommentButtonWrapper = styled(Collapse)({
  display: 'flex',
  alignItems: 'center',
});

const InputPictureWrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
});

const StyledPrimarySpinner = styled(PrimarySpinner)`
  margin: 20px auto;
`;

const StyledFetchMoreOnClick = styled(FetchMoreOnClick)`
  display: flex;
  justify-content: center;
  margin: 20px 0;
`;
